import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-black border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-[#A6A6B0]/50 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#6D00FF] focus:ring-1 focus:ring-[#6D00FF]/60";

const EMPTY = { name: "", email: "", company: "", phone: "", status: "lead", notes: "" };

const STATUS_CLS = {
  lead: "bg-[#6D00FF]/20 text-[#E8B6FF]",
  active: "bg-emerald-500/15 text-emerald-300",
  past: "text-[#A6A6B0] border border-white/10",
};

export const AdminClients = () => {
  const [clients, setClients] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
    setClients(data);
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name,
      email: c.email || "",
      company: c.company || "",
      phone: c.phone || "",
      status: c.status,
      notes: c.notes || "",
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email || null,
        company: form.company || null,
        phone: form.phone || null,
        status: form.status,
        notes: form.notes || null,
      };
      if (editing) {
        await axios.patch(`${API}/clients/${editing}`, payload, { withCredentials: true });
        toast.success("Client updated");
      } else {
        await axios.post(`${API}/clients`, payload, { withCredentials: true });
        toast.success("Client added");
      }
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error("Could not save client");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await axios.delete(`${API}/clients/${id}`, { withCredentials: true });
    toast.success("Client deleted");
    load();
  };

  if (!clients) return <p className="text-sm text-[#A6A6B0]">Loading clients…</p>;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight">
            Clients
          </h1>
          <p className="text-sm text-[#A6A6B0] mt-2">{clients.length} in your CRM</p>
        </div>
        <button
          data-testid="client-add-button"
          onClick={openNew}
          className="flex items-center gap-2 bg-[#6D00FF] hover:bg-[#8B2CFF] text-white text-xs font-bold uppercase tracking-[0.14em] px-5 py-3 rounded-md transition-colors duration-300"
        >
          <Plus className="w-4 h-4" /> Add Client
        </button>
      </div>

      {showForm && (
        <form
          data-testid="client-form"
          onSubmit={save}
          className="glass-card rounded-lg p-6 sm:p-8 mb-8 space-y-4"
        >
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-lg font-bold uppercase tracking-tight">
              {editing ? "Edit Client" : "New Client"}
            </h2>
            <button type="button" onClick={() => setShowForm(false)} aria-label="Close form">
              <X className="w-5 h-5 text-[#A6A6B0] hover:text-white" />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="client-name-input" required value={form.name} onChange={set("name")} placeholder="Name *" className={inputCls} />
            <input data-testid="client-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} />
            <input data-testid="client-company-input" value={form.company} onChange={set("company")} placeholder="Company" className={inputCls} />
            <input data-testid="client-phone-input" value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} />
            <select data-testid="client-status-select" value={form.status} onChange={set("status")} className={inputCls}>
              <option value="lead">Lead</option>
              <option value="active">Active</option>
              <option value="past">Past</option>
            </select>
          </div>
          <textarea data-testid="client-notes-input" rows={3} value={form.notes} onChange={set("notes")} placeholder="Notes — project context, preferences, history" className={`${inputCls} resize-none`} />
          <button
            data-testid="client-save-button"
            type="submit"
            disabled={saving}
            className="bg-[#6D00FF] hover:bg-[#8B2CFF] disabled:opacity-60 text-white text-xs font-bold uppercase tracking-[0.14em] px-6 py-3 rounded-md transition-colors duration-300"
          >
            {saving ? "Saving…" : editing ? "Update Client" : "Add Client"}
          </button>
        </form>
      )}

      {clients.length === 0 ? (
        <div className="glass-card rounded-lg p-16 text-center">
          <p className="text-[#A6A6B0] text-sm">No clients yet. Add your first one.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="clients-list">
          {clients.map((c, i) => (
            <div key={c.id} data-testid={`client-item-${i}`} className="glass-card rounded-lg px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">
                    {c.name}
                    {c.company ? <span className="text-[#A6A6B0] font-normal"> · {c.company}</span> : ""}
                  </p>
                  <p className="text-xs text-[#A6A6B0] mt-0.5">
                    {[c.email, c.phone].filter(Boolean).join(" · ") || "No contact details"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold uppercase tracking-[0.14em] rounded px-2.5 py-1 ${STATUS_CLS[c.status] || STATUS_CLS.lead}`}>
                    {c.status}
                  </span>
                  <button data-testid={`client-edit-${i}`} onClick={() => openEdit(c)} aria-label={`Edit ${c.name}`} className="p-2 text-[#A6A6B0] hover:text-white transition-colors duration-300">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`client-delete-${i}`} onClick={() => remove(c.id)} aria-label={`Delete ${c.name}`} className="p-2 text-[#A6A6B0] hover:text-[#C43CFF] transition-colors duration-300">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {c.notes && <p className="text-xs text-[#A6A6B0]/80 mt-3 leading-relaxed">{c.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
